import { useEffect, useState } from "react";
import { Loader2, Crown, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { ZODIAC_SIGNS } from "@/lib/readoo";

const Box = ({ children, testid }) => <div data-testid={testid} className="bg-surfaceSecondary border border-hair rounded-2xl p-5">{children}</div>;
const Num = ({ v, label, testid }) => (
  <div data-testid={testid}><p className="font-serif text-2xl font-semibold text-onSurface">{v}</p><p className="text-xs text-subtle">{label}</p></div>
);

// Conversione lettori → registrati e motivi dell'iscrizione (dalla CTA che l'ha generata).
export function ConversionCard() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); apiFetch(`/admin/conversion?days=${days}`, { auth: true }).then(setD).catch(() => setD({})); }, [days]);
  const max = Math.max(1, ...(d?.reasons || []).map((r) => r.count));
  return (
    <Box testid="conversion-card">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-onSurface"><TrendingUp size={18} className="text-brand" /> Conversione lettori → registrati</h2>
        <select data-testid="conversion-days" value={days} onChange={(e) => setDays(Number(e.target.value))} className="bg-surface border border-hair rounded-full px-3 py-1.5 text-xs text-onSurface">
          {[7, 30, 90].map((n) => <option key={n} value={n}>Ultimi {n} giorni</option>)}
        </select>
      </div>
      {!d ? <Loader2 className="animate-spin text-subtle" /> : (
        <>
          <div className="grid grid-cols-3 gap-4">
            <Num v={d.visitors ?? 0} label="Visitatori" testid="conversion-visitors" />
            <Num v={d.registrations ?? 0} label="Registrazioni" testid="conversion-registrations" />
            <Num v={`${d.conversion_rate ?? 0}%`} label="Conversion rate" testid="conversion-rate" />
          </div>
          {d.visitors_since && <p data-testid="conversion-since" className="mt-2 text-xs text-subtle">I visitatori si contano dal {new Date(d.visitors_since).toLocaleDateString("it-IT")}: prima di quella data il tasso non è confrontabile.</p>}
          <p className="mt-6 mb-2 text-xs font-mono uppercase tracking-wider text-subtle">Perché si sono registrati?</p>
          <ul data-testid="conversion-reasons" className="space-y-2">
            {(d.reasons || []).map((r) => (
              <li key={r.key} data-testid={`conversion-reason-${r.key}`} className="flex items-center gap-3 text-sm">
                <span className="w-32 shrink-0 text-onSurfaceSecondary">{r.label}</span>
                <span className="flex-1 h-2 rounded-full bg-surfaceTertiary overflow-hidden"><span className="block h-full bg-brand" style={{ width: `${(r.count / max) * 100}%` }} /></span>
                <span className="w-10 text-right font-mono text-onSurface">{r.count}</span>
              </li>
            ))}
          </ul>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            <Num v={d.saved_article_count ?? 0} label="Articoli salvati" testid="conversion-saved" />
            <Num v={d.onboarding_completed ?? 0} label="Onboarding completati" testid="conversion-onboarding" />
            <Num v={d.founder_count ?? 0} label="Founder" testid="conversion-founders" />
            <Num v={d.personalized_feed_clicks ?? 0} label="Click feed Per te" testid="conversion-foryou-clicks" />
            <Num v={d.coupon_clicks_registered ?? 0} label="Click coupon iscritti" testid="conversion-coupon-registered" />
            <Num v={d.coupon_clicks_guest ?? 0} label="Click coupon ospiti" testid="conversion-coupon-guest" />
          </div>
        </>
      )}
    </Box>
  );
}

// Founder Readoo: status non economico, attivabile e con data di fine facoltativa.
export function FounderAdminCard() {
  const [d, setD] = useState(null);
  const [until, setUntil] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { apiFetch(`/admin/founder`, { auth: true }).then((r) => { setD(r); setUntil((r.until || "").slice(0, 10)); }).catch(() => {}); }, []);
  const save = async (enabled) => {
    setBusy(true);
    try { const r = await apiFetch(`/admin/founder`, { method: "PUT", auth: true, body: { enabled, until: until || null } }); setD(r); toast.success("Founder Readoo aggiornato"); }
    catch (e) { toast.error(e.message || "Errore"); } finally { setBusy(false); }
  };
  if (!d) return null;
  return (
    <Box testid="founder-admin-card">
      <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-onSurface"><Crown size={18} className="text-brand" /> Founder Readoo</h2>
      <p data-testid="founder-admin-status" className="mt-1 text-sm text-subtle">
        {d.enabled ? "Attivo: chi si registra diventa Founder" : "Spento: i nuovi iscritti non diventano Founder"}
        {d.enabled && d.until ? ` fino al ${new Date(d.until).toLocaleDateString("it-IT")}` : d.enabled ? " (nessuna data di fine)" : ""} · <strong className="text-onSurface">{d.founder_count}</strong> Founder
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <label className="text-xs text-subtle" htmlFor="founder-until">Data di fine</label>
        <input id="founder-until" type="date" data-testid="founder-admin-until" value={until} onChange={(e) => setUntil(e.target.value)} className="bg-surface border border-hair rounded-full px-3 py-1.5 text-sm text-onSurface" />
        <button type="button" disabled={busy} onClick={() => save(true)} data-testid="founder-admin-save" className="px-4 py-2 rounded-full bg-brand text-onBrand text-xs font-semibold disabled:opacity-50">{d.enabled ? "Salva" : "Attiva"}</button>
        {d.enabled && <button type="button" disabled={busy} onClick={() => save(false)} data-testid="founder-admin-off" className="px-4 py-2 rounded-full border border-hair text-xs font-semibold text-onSurface disabled:opacity-50">Spegni</button>}
      </div>
      <FounderShares />
    </Box>
  );
}

function FounderShares() {
  const [days, setDays] = useState(30);
  const [s, setS] = useState(null);
  useEffect(() => { setS(null); apiFetch(`/admin/founder/shares?days=${days}`, { auth: true }).then(setS).catch(() => setS({})); }, [days]);
  return (
    <div data-testid="founder-shares" className="mt-6 pt-5 border-t border-hair">
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-xs font-mono uppercase tracking-wider text-subtle">Condivisioni del badge</p>
        <select data-testid="founder-shares-days" value={days} onChange={(e) => setDays(Number(e.target.value))} className="bg-surface border border-hair rounded-full px-3 py-1.5 text-xs text-onSurface">
          {[7, 30, 90].map((n) => <option key={n} value={n}>Ultimi {n} giorni</option>)}
        </select>
      </div>
      {!s ? <Loader2 className="animate-spin text-subtle" /> : (
        <>
          <div className="grid grid-cols-3 gap-4">
            <Num v={s.shares_total ?? 0} label="Condivisioni" testid="founder-shares-total" />
            <Num v={s.page_views ?? 0} label="Visite alla pagina Founder" testid="founder-shares-views" />
            <Num v={s.registrations ?? 0} label="Iscritti da quei link" testid="founder-shares-registrations" />
          </div>
          <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
            <Num v={s.shares?.whatsapp ?? 0} label="WhatsApp" testid="founder-shares-whatsapp" />
            <Num v={s.shares?.facebook ?? 0} label="Facebook" testid="founder-shares-facebook" />
            <Num v={s.shares?.link ?? 0} label="Link copiato" testid="founder-shares-link" />
          </div>
        </>
      )}
    </div>
  );
}

const yn = (v) => (v === null || v === undefined ? "—" : v ? "Sì" : "No");
const CTA_LABEL = { feed_per_te: "Feed Per te", salva: "Salva articolo", vantaggi: "Vantaggi", founder: "Founder", poll: "Domanda del giorno", coupon: "Coupon", altro: "Altro" };

export function UserProfileDetails({ p }) {
  if (!p) return null;
  const rows = [
    ["Interessi", p.interests.join(", ") || "—"], ["Squadre", p.teams.join(", ") || "—"], ["Città meteo", p.city || "—"],
    ["Segno", ZODIAC_SIGNS.find((z) => z.key === p.sign)?.name || "—"], ["Articoli salvati", p.saved], ["Founder", yn(p.founder)],
    ["Provenienza registrazione", [CTA_LABEL[p.signup_cta] || p.signup_cta, p.signup_source, p.utm?.utm_source].filter(Boolean).join(" · ") || "—"],
    ["Newsletter", yn(p.consent_newsletter)], ["Partner", yn(p.consent_partners)],
    ["Ultimo accesso", p.last_seen_at ? new Date(p.last_seen_at).toLocaleDateString("it-IT") : "—"],
  ];
  return (
    <dl data-testid="user-profile-details" className="mb-5 divide-y divide-hair/50 text-sm">
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-4 py-1.5"><dt className="text-subtle">{k}</dt><dd className="text-onSurface text-right">{v}</dd></div>
      ))}
    </dl>
  );
}
